import { useMemo, useState, useCallback } from 'react'
import ApiConnectPageHeader from '../components/api-connect/ApiConnectPageHeader'
import IntegrationToolbar from '../components/api-connect/IntegrationToolbar'
import CategorySection from '../components/api-connect/CategorySection'
import EmptyState from '../components/api-connect/EmptyState'
import {
  CATEGORIES,
  INTEGRATIONS,
  computeStats,
} from '../data/apiConnectData'

function filterIntegrations({
  items,
  search,
  activeTab,
  categoryFilter,
  statusFilter,
  sortBy,
}) {
  let list = [...items]

  if (activeTab === 'api') {
    list = list.filter((i) => i.connectivityMode === 'api')
  } else {
    list = list.filter((i) => i.connectivityMode === 'tracking')
  }

  if (categoryFilter !== 'all') {
    list = list.filter((i) => i.category === categoryFilter)
  }

  if (statusFilter !== 'all') {
    list = list.filter((i) => i.status === statusFilter)
  }

  const q = search.trim().toLowerCase()
  if (q) {
    list = list.filter((i) => {
      const hay = [i.name, i.description, i.type, ...(i.tags || [])].join(' ').toLowerCase()
      return hay.includes(q)
    })
  }

  if (sortBy === 'az') {
    list.sort((a, b) => a.name.localeCompare(b.name))
  } else if (sortBy === 'most_used') {
    list.sort((a, b) => (b.usageRank || 0) - (a.usageRank || 0))
  } else if (sortBy === 'recent') {
    list.sort((a, b) => a.id.localeCompare(b.id))
  }

  return list
}

export default function ApiConnect() {
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState('api')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('most_used')
  const [view, setView] = useState('grid')

  const filtered = useMemo(
    () => filterIntegrations({
      items: INTEGRATIONS,
      search,
      activeTab,
      categoryFilter,
      statusFilter,
      sortBy,
    }),
    [search, activeTab, categoryFilter, statusFilter, sortBy],
  )

  const stats = useMemo(() => computeStats(INTEGRATIONS), [])

  const grouped = useMemo(() => {
    const map = new Map()
    CATEGORIES.forEach((c) => map.set(c.id, []))
    filtered.forEach((item) => {
      if (map.has(item.category)) map.get(item.category).push(item)
    })
    return CATEGORIES.map((c) => ({ category: c, items: map.get(c.id) || [] }))
      .filter((g) => g.items.length > 0)
  }, [filtered])

  const resetFilters = useCallback(() => {
    setSearch('')
    setCategoryFilter('all')
    setStatusFilter('all')
    setSortBy('most_used')
    setActiveTab('api')
  }, [])

  const handleAction = useCallback((item) => {
    // Placeholder for future connect/configure flows
    console.info('[API Connect]', item.actionLabel, item.name)
  }, [])

  return (
    <div className="min-h-full bg-slate-50 dark:bg-slate-950">
      <div className="mx-auto max-w-[1600px] space-y-5 px-4 pb-10 pt-5 sm:px-6">
        <ApiConnectPageHeader stats={stats} />

        <IntegrationToolbar
          search={search}
          onSearchChange={setSearch}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          sortBy={sortBy}
          onSortChange={setSortBy}
          view={view}
          onViewChange={setView}
          categories={CATEGORIES}
          resultCount={filtered.length}
        />

        {grouped.length === 0 ? (
          <EmptyState onReset={resetFilters} />
        ) : (
          <div className="space-y-5">
            {grouped.map(({ category, items }) => (
              <CategorySection
                key={category.id}
                category={category}
                items={items}
                view={view}
                onAction={handleAction}
              />
            ))}
          </div>
        )}

        <p className="pt-2 text-center text-[11px] text-slate-400 dark:text-slate-500">
          v1.22.25 · info-x.ai enterprise TMS
        </p>
      </div>
    </div>
  )
}
